import {
  useAuth,
  useOrganization,
  useOrganizationList,
} from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router-dom';

import { useOnlineStatusTracker } from '@/hooks/user/useOnlineStatusTracker';
import { useGetZorGeneralSetting } from '@/hooks/zor-general-setting/useGetZorGeneralSetting';
import { MainLayout } from '@/layouts/MainLayout';
import { DashboardAskAimei } from '@/pages/dashboard/ask-aimei';
import { DashboardDiscovery } from '@/pages/dashboard/discovery';
import { DashboardFinancialOverview } from '@/pages/dashboard/financial-overview/FinancialOverview';
import { DashboardHome } from '@/pages/dashboard/Home';
import { DashboardLibrary } from '@/pages/dashboard/library';
import { DashboardProfile } from '@/pages/dashboard/profile';
import { DashboardTerritories } from '@/pages/dashboard/territories';
import logger from '@/services/logger.service';

const TOKEN_TEMPLATE = 'ff-backend-new' as const;
const TOKEN_STORAGE_KEY = 'access-token' as const;

export const DashboardRoutes = () => {
  // custom hooks
  const { getToken } = useAuth();
  const { organization } = useOrganization();
  const { isLoaded, userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true, keepPreviousData: true },
  });
  const { startTracking, stopTracking } = useOnlineStatusTracker({
    interval: 5 * 60 * 1000, // Update every 5 minutes
    trackActivity: true,
    activityThrottle: 60 * 1000, // Throttle activity updates to once per minute
  });

  // Load ZOR general settings (including brand colors)
  useGetZorGeneralSetting();

  // Track previous organization to detect changes
  const previousOrganizationId = useRef<string | null>(null);

  // event handlers
  const updateToken = async () => {
    const token = await getToken({ template: TOKEN_TEMPLATE });
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token || '');
  };

  // effects
  useEffect(() => {
    if (organization) {
      window.localStorage.setItem('active-org-id', organization.id);

      void updateToken();
      // Start tracking user's online status when accessing dashboard
      startTracking();
    }

    // Cleanup tracking when component unmounts or organization changes
    return () => {
      stopTracking();
    };
  }, [organization, startTracking, stopTracking]);

  useEffect(() => {
    if (isLoaded && !organization && userMemberships?.data?.length) {
      console.log('userMemberships', userMemberships);
      setActive({
        organization: window.localStorage.getItem('active-org-id') || '',
      });
    }
  }, [isLoaded, organization, userMemberships, setActive]);

  // Handle organization switching - update token and reload page when organization changes
  useEffect(() => {
    if (organization?.id && isLoaded) {
      if (
        previousOrganizationId.current !== null &&
        previousOrganizationId.current !== organization.id
      ) {
        const updateTokenAndReload = async () => {
          try {
            const token = await getToken({ template: TOKEN_TEMPLATE });
            if (token) {
              window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
            }
          } catch (error) {
            logger.error(
              'Failed to update access token before reload',
              error as Error,
              {
                organizationId: organization?.id,
                previousOrganizationId: previousOrganizationId.current,
              }
            );
          } finally {
            window.location.reload();
          }
        };

        void updateTokenAndReload();
      }

      // Update the previous organization id
      previousOrganizationId.current = organization.id;
    }
  }, [organization?.id, isLoaded, getToken]);

  return (
    <MainLayout>
      <Routes>
        <Route path="/" element={<DashboardHome />} />
        <Route path="/discovery/:stageId" element={<DashboardDiscovery />} />
        <Route path="/profile" element={<DashboardProfile />} />
        <Route path="/library" element={<DashboardLibrary />} />
        <Route path="/territories" element={<DashboardTerritories />} />
        <Route
          path="/financial-overview"
          element={<DashboardFinancialOverview />}
        />
        <Route path="/ask-aimei" element={<DashboardAskAimei />} />
      </Routes>
    </MainLayout>
  );
};
